import React, { useState, useCallback, useRef, useEffect } from 'react';
import './GlobalContextMenu.css';
import { ExamineModal } from './ExamineModal';
import {
  getIslandExamineText,
  getFishingSpotExamineText,
  getShipExamineText,
  getWhirlpoolExamineText,
  getLootContainerExamineText,
  getResourceNodeExamineText,
  getAmbientNPCExamineText,
  getDebrisExamineText,
  getShipwreckExamineText,
  getWorldExamineText,
  ExamineText,
} from '../utils/ExamineFlavorText';

export interface ContextMenuContent {
  type: 'fishing' | 'island' | 'debris' | 'ship' | 'ambient' | 'world' | 'resource' | 'lootcontainer' | 'whirlpool' | 'shipwreck' | 'riftscar' | 'sanctum_container';
  data: Record<string, any>;
}

interface ExamineState {
  visible: boolean;
  data: ExamineText | null;
}

interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  content: ContextMenuContent | null;
}

// Global event emitter for context menu
class ContextMenuEmitter {
  private listeners: Array<(content: ContextMenuContent, x: number, y: number) => void> = [];
  private hideListeners: Array<() => void> = [];

  on(callback: (content: ContextMenuContent, x: number, y: number) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  onHide(callback: () => void) {
    this.hideListeners.push(callback);
    return () => {
      this.hideListeners = this.hideListeners.filter(l => l !== callback);
    };
  }

  show(content: ContextMenuContent, x: number, y: number) {
    console.log(`📌 Context menu emitter.show() called with type: ${content.type}, pos: (${x}, ${y})`);
    this.listeners.forEach(l => l(content, x, y));
  }

  hide() {
    this.hideListeners.forEach(l => l());
  }
}

export const contextMenuEmitter = new ContextMenuEmitter();

interface GlobalContextMenuProviderProps {
  children: React.ReactNode;
  gameRef: React.MutableRefObject<any>;
  socketRef: React.MutableRefObject<any>;
  onFishingSpotSelect: (spotId: string, spotType: string, spotPosition: { x: number; y: number }) => void;
  onIslandSelect: (islandId: string, islandPosition: { x: number; y: number }, canDock: boolean, isDocked: boolean, isDockedHere: boolean) => void;
  onLootContainerSelect: (containerId: string) => void;
  onResourceHarvest: (nodeId: string) => void;
  onWhirlpoolSelect: (whirlpoolId: string, whirlpoolPosition: { x: number; y: number }, whirlpoolClass: string) => void;
}

export const GlobalContextMenuProvider: React.FC<GlobalContextMenuProviderProps> = ({
  children,
  gameRef,
  socketRef,
  onFishingSpotSelect,
  onIslandSelect,
  onLootContainerSelect,
  onResourceHarvest,
  onWhirlpoolSelect,
}) => {
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    content: null,
  });
  const [examineModal, setExamineModal] = useState<ExamineState>({
    visible: false,
    data: null,
  });
  const menuRef = useRef<HTMLDivElement>(null);

  const hideContextMenu = useCallback(() => {
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      content: null,
    });
  }, []);

  // Listen for global context menu events
  useEffect(() => {
    const unsubscribe = contextMenuEmitter.on((content, x, y) => {
      console.log(`🎨 GlobalContextMenu: Setting visible=true for type: ${content.type} at (${x}, ${y})`);
      setContextMenu({
        visible: true,
        x: x + 4,
        y: y + 4,
        content,
      });
    });

    const unsubscribeHide = contextMenuEmitter.onHide(() => {
      hideContextMenu();
    });

    return () => {
      unsubscribe();
      unsubscribeHide();
    };
  }, [hideContextMenu]);

  // Clamp context menu position to viewport
  useEffect(() => {
    if (!contextMenu.visible || !menuRef.current) return;

    const rect = menuRef.current.getBoundingClientRect();
    const menuWidth = rect.width;
    const menuHeight = rect.height;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 8;

    let nextX = contextMenu.x;
    let nextY = contextMenu.y;

    // Clamp horizontally
    if (nextX + menuWidth + padding > viewportWidth) {
      nextX = viewportWidth - menuWidth - padding;
    }
    if (nextX < padding) {
      nextX = padding;
    }

    // Clamp vertically
    if (nextY + menuHeight + padding > viewportHeight) {
      nextY = viewportHeight - menuHeight - padding;
    }
    if (nextY < padding) {
      nextY = padding;
    }

    if (nextX !== contextMenu.x || nextY !== contextMenu.y) {
      setContextMenu((prev) => ({
        ...prev,
        x: nextX,
        y: nextY,
      }));
    }
  }, [contextMenu.visible, contextMenu.x, contextMenu.y]);

  // Close menu on outside click
  useEffect(() => {
    const handleClick = () => {
      hideContextMenu();
    };

    if (contextMenu.visible) {
      document.addEventListener('click', handleClick);
      return () => {
        document.removeEventListener('click', handleClick);
      };
    }
  }, [contextMenu.visible, hideContextMenu]);

  const renderMenuContent = () => {
    if (!contextMenu.content) return null;

    const { type, data } = contextMenu.content;
    const { isIslandResearched } = require('../hooks/useGameUtilities');
    const { formatDisplayName } = require('../utils/formatDisplayName');

    const handleClose = () => hideContextMenu();

    const handleExamine = (examineData: ExamineText) => {
      setExamineModal({ visible: true, data: examineData });
      handleClose();
    };

    switch (type) {
      case 'fishing':
        return (
          <>
            <div className="context-menu-header">Fishing Spot</div>
            <button
              className="context-menu-item"
              onClick={() => {
                if (data?.spotId) {
                  onFishingSpotSelect(data.spotId, data.spotType || null, data.spotPosition || null);
                }
                handleClose();
              }}
            >
              Analyze Waters
            </button>
            <button
              className="context-menu-item"
              onClick={() => handleExamine(getFishingSpotExamineText(data?.spotType || 'unknown'))}
            >
              Examine
            </button>
            <button className="context-menu-item" onClick={handleClose}>Cancel</button>
          </>
        );

      case 'ambient':
        return (
          <>
            <div className="context-menu-header">
              {String(data?.ambientDisplayName || data?.ambientType || 'Ambient NPC')}
            </div>
            {(data?.ambientType === 'sailor' || data?.ambientType === 'fisherman') && (
              <>
                <button
                  className="context-menu-item"
                  onClick={() => {
                    const scene = gameRef.current?.scene.getScene('OceanScene') as any;
                    if (data?.ambientId) {
                      scene?.requestAmbientTrade?.(data.ambientId);
                    }
                    handleClose();
                  }}
                >
                  Trade
                </button>
                <button
                  className="context-menu-item"
                  onClick={() => {
                    const scene = gameRef.current?.scene.getScene('OceanScene') as any;
                    if (data?.ambientId) {
                      scene?.attackAmbient?.(data.ambientId);
                    }
                    handleClose();
                  }}
                >
                  Attack
                </button>
              </>
            )}
            <button
              className="context-menu-item"
              onClick={() => handleExamine(getAmbientNPCExamineText(data?.ambientType || 'sailor', data?.ambientDisplayName))}
            >
              Examine
            </button>
            <button className="context-menu-item" onClick={handleClose}>Cancel</button>
          </>
        );

      case 'lootcontainer':
        return (
          <>
            <div className="context-menu-header">Wreck</div>
            <button
              className="context-menu-item"
              onClick={() => {
                if (data?.lootContainerId) {
                  onLootContainerSelect(data.lootContainerId);
                }
                handleClose();
              }}
            >
              Loot Wreck
            </button>
            <button
              className="context-menu-item"
              onClick={() => handleExamine(getLootContainerExamineText())}
            >
              Examine
            </button>
            <button className="context-menu-item" onClick={handleClose}>Cancel</button>
          </>
        );

      case 'resource':
        return (
          <>
            <div className="context-menu-header">{data?.resourceNodeName || 'Resource Node'}</div>
            <button
              className="context-menu-item"
              onClick={() => {
                if (data?.resourceNodeId) {
                  onResourceHarvest(data.resourceNodeId);
                }
                handleClose();
              }}
            >
              Harvest
            </button>
            <button
              className="context-menu-item"
              onClick={() => handleExamine(getResourceNodeExamineText(data?.resourceType || 'wood'))}
            >
              Examine
            </button>
            <button className="context-menu-item" onClick={handleClose}>Cancel</button>
          </>
        );

      case 'island':
        return (
          <>
            <div className="context-menu-header">Island</div>
            {data?.canDock && !data.isDocked && (
              <button
                className="context-menu-item"
                onClick={() => {
                  const scene = gameRef.current?.scene.getScene('OceanScene') as any;
                  if (data?.islandId) {
                    scene?.dockAtIsland?.(data.islandId);
                  }
                  handleClose();
                }}
              >
                Dock
              </button>
            )}
            <button
              className="context-menu-item"
              onClick={() => {
                if (data?.islandId) {
                  onIslandSelect(data.islandId, data.islandPosition || null, data.canDock || false, data.isDocked || false, data.isDockedHere || false);
                }
                handleClose();
              }}
            >
              {data?.islandId && isIslandResearched(data.islandId) ? 'Interact' : 'Research Island'}
            </button>
            <button
              className="context-menu-item"
              onClick={() => handleExamine(getIslandExamineText({
                islandId: data?.islandId,
                permanentName: data?.permanentName,
                isPermanent: data?.isPermanent,
                biome: data?.biome,
                size: data?.size,
              }))}
            >
              Examine
            </button>
            <button className="context-menu-item" onClick={handleClose}>Cancel</button>
          </>
        );

      case 'debris':
        return (
          <>
            <div className="context-menu-header">Message in a Bottle</div>
            <button
              className="context-menu-item"
              onClick={() => {
                if (data?.debrisId) {
                  socketRef.current?.emit('bottle:collect', { id: data.debrisId });
                }
                handleClose();
              }}
            >
              Collect
            </button>
            <button
              className="context-menu-item"
              onClick={() => handleExamine(getDebrisExamineText())}
            >
              Examine
            </button>
            <button className="context-menu-item" onClick={handleClose}>Cancel</button>
          </>
        );

      case 'ship':
        return (
          <>
            <div style={{ display: 'flex', gap: '12px', padding: '8px 12px', borderBottom: '1px solid #30363d' }}>
              <div style={{ flexShrink: 0 }}>
                <img
                  src={
                    data?.shipData?.isNPC
                      ? `/assets/portraits/marines/marine_${(((data?.shipId || '').charCodeAt(0) || 0) % 5) + 1}.png`
                      : `/assets/portraits/pirates/pirate_${(((data?.shipId || '').charCodeAt(0) || 0) % 16) + 1}.png`
                  }
                  alt="Portrait"
                  style={{
                    width: '48px',
                    height: '48px',
                    border: '2px solid #30363d',
                    imageRendering: 'pixelated',
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = `/assets/portraits/pirates/pirate_${Math.floor(Math.random() * 16) + 1}.png`;
                  }}
                />
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ color: '#e2e8f0', fontSize: '12px', fontWeight: 700 }}>
                  {data?.shipData?.ownerName
                    ? formatDisplayName({
                        baseName: data.shipData.ownerName,
                        fleetTag: data.shipData.fleetTag,
                        titleName: data.shipData.titleName,
                      })
                    : (data?.shipData?.name || 'Ship')}
                </div>
                {data?.shipData?.level && (
                  <div style={{ color: '#888', fontSize: '10px' }}>Lv.{data.shipData.level}</div>
                )}
              </div>
            </div>
            <button
              className="context-menu-item"
              onClick={() => {
                if (data?.shipId) {
                  const scene = gameRef.current?.scene.getScene('OceanScene') as any;
                  scene?.engageTarget?.(data.shipId);
                }
                handleClose();
              }}
            >
              Engage
            </button>
            <button
              className="context-menu-item"
              onClick={() => {
                console.log('Investigate ship:', data?.shipId);
                handleClose();
              }}
            >
              Investigate
            </button>
            <button
              className="context-menu-item"
              onClick={() => handleExamine(getShipExamineText({
                isNPC: data?.shipData?.isNPC,
                level: data?.shipData?.level,
                ownerName: data?.shipData?.ownerName,
                shipType: data?.shipData?.shipType,
                faction: data?.shipData?.faction,
              }))}
            >
              Examine
            </button>
            <button className="context-menu-item" onClick={handleClose}>Cancel</button>
          </>
        );

      case 'world':
        return (
          <>
            <div className="context-menu-header">Navigation</div>
            <button
              className="context-menu-item"
              onClick={() => {
                if (data?.worldX !== undefined && data?.worldY !== undefined) {
                  const scene = gameRef.current?.scene.getScene('OceanScene') as any;
                  scene?.disengageTarget?.();
                  scene?.moveToPosition?.(data.worldX, data.worldY);
                }
                handleClose();
              }}
            >
              Travel Here
            </button>
            <button
              className="context-menu-item"
              onClick={() => handleExamine(getWorldExamineText())}
            >
              Examine
            </button>
            <button className="context-menu-item" onClick={handleClose}>Cancel</button>
          </>
        );

      case 'shipwreck':
        return (
          <>
            <div className="context-menu-header">Shipwreck [{data?.shipwreckQuality || 'Unknown'}]</div>
            <button
              className="context-menu-item"
              onClick={() => {
                if (data?.shipwreckId) {
                  socketRef.current?.emit('shipwreck:startSalvage', { wreckId: data.shipwreckId });
                }
                handleClose();
              }}
            >
              Salvage Wreck
            </button>
            <button
              className="context-menu-item"
              onClick={() => {
                if (data?.shipwreckPosition) {
                  const scene = gameRef.current?.scene.getScene('OceanScene') as any;
                  scene?.disengageTarget?.();
                  scene?.moveToPosition?.(data.shipwreckPosition.x, data.shipwreckPosition.y);
                }
                handleClose();
              }}
            >
              Travel Here
            </button>
            <button
              className="context-menu-item"
              onClick={() => handleExamine(getShipwreckExamineText(data?.shipwreckQuality || 'common'))}
            >
              Examine
            </button>
            <button className="context-menu-item" onClick={handleClose}>Cancel</button>
          </>
        );

      case 'whirlpool':
        return (
          <>
            <div className="context-menu-header">Whirlpool [{data?.whirlpoolClass || '?'}]</div>
            <button
              className="context-menu-item"
              onClick={() => {
                if (data?.whirlpoolId) {
                  onWhirlpoolSelect(data.whirlpoolId, data.whirlpoolPosition || null, data.whirlpoolClass || '?');
                }
                handleClose();
              }}
            >
              Scan Whirlpool
            </button>
            <button
              className="context-menu-item"
              onClick={() => {
                if (data?.whirlpoolPosition) {
                  const scene = gameRef.current?.scene.getScene('OceanScene') as any;
                  scene?.disengageTarget?.();
                  scene?.moveToPosition?.(data.whirlpoolPosition.x, data.whirlpoolPosition.y);
                }
                handleClose();
              }}
            >
              Sail To
            </button>
            <button
              className="context-menu-item"
              onClick={() => handleExamine(getWhirlpoolExamineText(data?.whirlpoolClass || 'D'))}
            >
              Examine
            </button>
            <button className="context-menu-item" onClick={handleClose}>Cancel</button>
          </>
        );

      default:
        return null;
    }
  };

  // Log when rendering
  if (contextMenu.visible && contextMenu.content) {
    console.log(`✅ GlobalContextMenu: RENDERING at (${contextMenu.x}, ${contextMenu.y}), type: ${contextMenu.content.type}`);
  }

  return (
    <>
      {children}
      {contextMenu.visible && contextMenu.content && (
        <div
          ref={menuRef}
          className="global-context-menu"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
            position: 'fixed',
            zIndex: 10001,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {renderMenuContent()}
        </div>
      )}
      {examineModal.visible && (
        <ExamineModal
          examineData={examineModal.data}
          onClose={() => setExamineModal({ visible: false, data: null })}
        />
      )}
    </>
  );
};
